/**
 * Email documents — send any generated PDF (resume, French version, a job's tailored
 * resume / cover letter / interview prep) to the client or an employer. Sent from the
 * platform with the adviser's name; replies go to the adviser's email.
 *
 * Backend: POST /resumes/case/:threadId/email (server renders the PDFs, SMTP_Service sends).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import useAuthenticatedAxios from '../../../../auth/AuthenticatedAxios';
import { useToast } from '../../../../Context/ToastContext';
import {
  Overlay, Shell, TopBar, Title, Spacer, Row, Field, Label, Hint, Input, TextArea, Btn, IconBtn, Check, Banner,
} from './resumeStyles';
import { errMsg } from './resumeUtils';

export default function EmailDocumentsDialog({ threadId, clientEmail, clientName, hasFrench, preset = {}, onClose }) {
  const api = useAuthenticatedAxios();
  const { showToast } = useToast();
  const [jobs, setJobs] = useState([]);
  const [toClient, setToClient] = useState(preset.toClient ?? !preset.jobId);
  const [to, setTo] = useState(preset.toClient ?? !preset.jobId ? clientEmail || '' : '');
  const [recipientName, setRecipientName] = useState(preset.toClient ?? !preset.jobId ? clientName || '' : '');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [picked, setPicked] = useState({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/resumes/case/${threadId}/jobs`); setJobs(r.data.data.jobs || []); }
      catch { setJobs([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  // Everything that can be attached right now.
  const options = useMemo(() => {
    const o = [{ key: 'resume', kind: 'resume', label: 'Resume (English)' }];
    if (hasFrench) o.push({ key: 'resume_fr', kind: 'resume_fr', label: 'CV (French)' });
    for (const j of jobs) {
      const name = [j.title, j.company].filter(Boolean).join(' — ') || 'Job';
      if (j.tailoredResume) o.push({ key: `job_resume:${j.id}`, kind: 'job_resume', jobId: j.id, label: `Tailored resume · ${name}` });
      if (j.coverLetter) o.push({ key: `cover_letter:${j.id}`, kind: 'cover_letter', jobId: j.id, label: `Cover letter · ${name}` });
      if (j.interviewPrep) o.push({ key: `interview_prep:${j.id}`, kind: 'interview_prep', jobId: j.id, label: `Interview prep · ${name}` });
    }
    return o;
  }, [jobs, hasFrench]);

  // Pre-select from the preset (e.g. a job's documents, or just its interview prep).
  useEffect(() => {
    const sel = {};
    for (const o of options) {
      if (preset.jobId) {
        if (o.jobId === preset.jobId && (!preset.kinds || preset.kinds.includes(o.kind))) sel[o.key] = true;
      } else if (o.kind === 'resume') sel[o.key] = true;
    }
    if (Object.keys(sel).length) setPicked(sel);
    const job = jobs.find(j => j.id === preset.jobId);
    if (!subject) {
      if (preset.kinds?.includes('interview_prep') && job) setSubject(`Your interview preparation — ${[job.title, job.company].filter(Boolean).join(' at ')}`);
      else if (job && !toClient) setSubject(`Candidate application — ${[job.title, job.company].filter(Boolean).join(', ')}${clientName ? ` — ${clientName}` : ''}`);
      else setSubject(clientName ? `Resume — ${clientName}` : 'Your documents');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const chooseClient = (v) => {
    setToClient(v);
    setTo(v ? clientEmail || '' : '');
    setRecipientName(v ? clientName || '' : '');
  };

  const send = async () => {
    const documents = options.filter(o => picked[o.key]).map(o => ({ kind: o.kind, jobId: o.jobId }));
    if (!documents.length) return showToast('Choose at least one document', 'warning');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to.trim())) return showToast('Enter a valid email', 'warning');
    setSending(true);
    try {
      await api.post(`/resumes/case/${threadId}/email`, { to: to.trim(), recipientName, subject, message, documents });
      showToast(`Sent to ${to.trim()}`, 'success');
      onClose();
    } catch (e) { showToast(errMsg(e, 'Could not send'), 'error'); }
    finally { setSending(false); }
  };

  return createPortal(
    <Overlay style={{ zIndex: 5200 }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <Shell style={{ maxWidth: 680, alignSelf: 'center', maxHeight: '92vh' }}>
        <TopBar>
          <Title>Email documents</Title><Spacer />
          <Btn onClick={send} disabled={sending}>{sending ? 'Sending…' : 'Send'}</Btn>
          <IconBtn title="Close" onClick={onClose}>×</IconBtn>
        </TopBar>
        <div style={{ overflowY: 'auto', padding: 18 }}>
          <Row>
            <Check><input type="radio" checked={toClient} onChange={() => chooseClient(true)} />To the client</Check>
            <Check><input type="radio" checked={!toClient} onChange={() => chooseClient(false)} />To an employer / someone else</Check>
          </Row>
          {!toClient && <Banner style={{ marginTop: 10 }}>Check the resume's contact setting first — with “Hide — employers contact my company”, the employer sees your contact details instead of the candidate's.</Banner>}
          <Row $mt>
            <Field><Label>Email</Label><Input value={to} onChange={e => setTo(e.target.value)} placeholder="name@example.com" /></Field>
            <Field><Label>Name (optional)</Label><Input value={recipientName} onChange={e => setRecipientName(e.target.value)} /></Field>
          </Row>
          <Row $mt><Field><Label>Subject</Label><Input value={subject} onChange={e => setSubject(e.target.value)} /></Field></Row>
          <Row $mt><Field><Label>Message</Label><TextArea $h={110} value={message} onChange={e => setMessage(e.target.value)} placeholder="Optional — shown above the attachments." /></Field></Row>
          <Label style={{ display: 'block', marginTop: 16, marginBottom: 6 }}>Attach</Label>
          {options.map(o => (
            <div key={o.key} style={{ margin: '6px 0' }}>
              <Check><input type="checkbox" checked={!!picked[o.key]} onChange={e => setPicked(p => ({ ...p, [o.key]: e.target.checked }))} />{o.label}</Check>
            </div>
          ))}
          <Hint style={{ marginTop: 10 }}>PDFs are generated from the latest SAVED versions. Replies go to your email.</Hint>
        </div>
      </Shell>
    </Overlay>,
    document.body
  );
}
